/**
 * Services Hub — top-level category hub for /services.
 * Cross-references all .md files under content/services/
 *
 * Top-level services (10):
 *   /services/brand-protection-augmentation
 *   /services/competitive-intelligence
 *   /services/content-narrative-engineering
 *   /services/crisis-management-response
 *   /services/digital-intelligence-monitoring
 *   /services/influencer-network-orchestration
 *   /services/media-relations-pr
 *   /services/reputation-management
 *   /services/search-dominance-seo
 *   /services/social-media-intelligence
 *
 * Subcategory hubs (9) — see services-hub-subcategories.ts
 */

export const servicesHubMeta = {
  title: "AI-Powered Perception Services | Perception X2 by CryptoMize",
  description:
    "Autonomous perception services across brand protection, crisis management, narrative engineering, intelligence monitoring, influencer orchestration, media relations, and search dominance. 384×–1,416× faster response. 85–95% success rate.",
  canonical: "https://perception.ac/services",
  slug: "services",
};

export interface ServiceChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const servicesTopLevel: ServiceChild[] = [
  {
    code: "S01",
    title: "Brand Protection & Augmentation",
    description: "Defend and amplify brand equity across 50+ digital platforms. 300–500% narrative penetration with 95–98% organic authenticity.",
    href: "/services/brand-protection-augmentation",
    icon: "ShieldCheck",
    accent: "crimson",
  },
  {
    code: "S02",
    title: "Competitive Intelligence",
    description: "Map competitor narratives, anticipate strategic moves, and engineer perceptual advantage before the market reacts.",
    href: "/services/competitive-intelligence",
    icon: "Binoculars",
    accent: "purple",
  },
  {
    code: "S03",
    title: "Content & Narrative Engineering",
    description: "AI-powered content generation, narrative architecture, and large-scale message distribution at organic authenticity.",
    href: "/services/content-narrative-engineering",
    icon: "Sparkles",
    accent: "cyan",
  },
  {
    code: "S04",
    title: "Crisis Management & Response",
    description: "Autonomous crisis containment. 384×–1,416× faster response. 85–95% success rate across 500+ pre-built playbooks.",
    href: "/services/crisis-management-response",
    icon: "Siren",
    accent: "amber",
  },
  {
    code: "S05",
    title: "Digital Intelligence Monitoring",
    description: "Continuous monitoring across social, search, news, forums, reviews, and emerging surfaces — 144-dimensional sentiment tracking.",
    href: "/services/digital-intelligence-monitoring",
    icon: "Radar",
    accent: "emerald",
  },
  {
    code: "S06",
    title: "Influencer Network Orchestration",
    description: "Tier-coordinated influencer relationships — from nano-influencers to thought leaders — with calibrated authenticity.",
    href: "/services/influencer-network-orchestration",
    icon: "Network",
    accent: "violet",
  },
  {
    code: "S07",
    title: "Media Relations & PR",
    description: "Strategic media engagement, journalist mapping, and engineered coverage across global news, broadcast, and digital outlets.",
    href: "/services/media-relations-pr",
    icon: "Megaphone",
    accent: "denim",
  },
  {
    code: "S08",
    title: "Reputation Management",
    description: "Continuous reputation engineering, threat neutralization, and rehabilitation — across stakeholders, geographies, and surfaces.",
    href: "/services/reputation-management",
    icon: "Award",
    accent: "rose",
  },
  {
    code: "S09",
    title: "Search Dominance & SEO",
    description: "Engineered search presence — content, links, and authority — calibrated to dominate page-one SERPs at scale.",
    href: "/services/search-dominance-seo",
    icon: "Search",
    accent: "purple",
  },
  {
    code: "S10",
    title: "Social Media Intelligence",
    description: "Cross-platform social analytics, predictive engagement, and engineered amplification — at computational scale.",
    href: "/services/social-media-intelligence",
    icon: "Share2",
    accent: "cyan",
  },
];

export interface ServiceSubcategory {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
  children: { title: string; href: string }[];
}

export const servicesSubcategories: ServiceSubcategory[] = [
  {
    code: "SC01",
    title: "Brand Protection",
    description: "Defensive and offensive brand strategy — from augmentation to competitive intelligence at the brand level.",
    href: "/services/brand-protection",
    icon: "Shield",
    accent: "crimson",
    children: [
      { title: "Brand Augmentation", href: "/services/brand-augmentation" },
      { title: "Competitor Analysis", href: "/services/competitor-analysis" },
    ],
  },
  {
    code: "SC02",
    title: "Content Engineering",
    description: "Narrative architecture, content creation, and mass communication — engineered for organic resonance.",
    href: "/services/content-engineering",
    icon: "FileText",
    accent: "purple",
    children: [
      { title: "Buzz Marketing", href: "/services/buzz-marketing" },
      { title: "Content Creation", href: "/services/content-narrative-engineering/content-creation" },
      { title: "Mass Communication", href: "/services/mass-communication" },
    ],
  },
  {
    code: "SC03",
    title: "Crisis Management",
    description: "Pre-crisis hardening, real-time response, and post-event rehabilitation — operating at machine speed.",
    href: "/services/crisis-management",
    icon: "Siren",
    accent: "amber",
    children: [
      { title: "Crisis Prevention", href: "/services/crisis-prevention" },
      { title: "Crisis Response", href: "/services/crisis-response" },
    ],
  },
  {
    code: "SC04",
    title: "Digital Intelligence",
    description: "Listening, analysis, and prediction across every digital surface — sentiment, dialogue, reviews, search, and trends.",
    href: "/services/digital-intelligence",
    icon: "Radar",
    accent: "emerald",
    children: [
      { title: "Dialogue Control", href: "/services/dialogue-control" },
      { title: "Digital Listening", href: "/services/digital-listening" },
      { title: "Review Management", href: "/services/review-management" },
      { title: "Search Management", href: "/services/search-management" },
      { title: "Sentiment Analysis", href: "/services/sentiment-analysis" },
      { title: "Trend Management", href: "/services/trend-management" },
    ],
  },
  {
    code: "SC05",
    title: "Influencer Orchestration",
    description: "Coordinated influencer ecosystems — from nano-tier engagement to large-scale strategic amplification.",
    href: "/services/influencer-orchestration",
    icon: "Network",
    accent: "violet",
    children: [
      { title: "Influencer Marketing", href: "/services/influencer-marketing" },
      { title: "Online Identity Management", href: "/services/online-identity-management" },
    ],
  },
  {
    code: "SC06",
    title: "Media Relations",
    description: "Strategic communication for enterprises, governments, public figures, and political organizations.",
    href: "/services/media-relations",
    icon: "Mic",
    accent: "denim",
    children: [
      { title: "Enterprise Communications", href: "/services/enterprise-communications" },
      { title: "Government Communications", href: "/services/government-communications" },
      { title: "Media Monitoring", href: "/services/media-monitoring" },
      { title: "Political Communications", href: "/services/political-communications" },
      { title: "Public Figure Communications", href: "/services/public-figure-communications" },
    ],
  },
  {
    code: "SC07",
    title: "Reputation Management",
    description: "Comprehensive reputation engineering — monitoring, analysis, defense, and rehabilitation.",
    href: "/services/reputation-management",
    icon: "Award",
    accent: "rose",
    children: [
      { title: "Damage Control", href: "/services/damage-control" },
      { title: "Online Reputation Monitoring", href: "/services/online-reputation-monitoring" },
      { title: "Reputation Analysis", href: "/services/reputation-analysis" },
      { title: "Review Optimization", href: "/services/review-optimization" },
      { title: "Threat Analysis", href: "/services/threat-analysis" },
    ],
  },
  {
    code: "SC08",
    title: "Search Dominance",
    description: "Search presence engineered for dominance — SEO, SEM, link authority, and viral mechanics.",
    href: "/services/search-dominance",
    icon: "Search",
    accent: "purple",
    children: [
      { title: "Link Building", href: "/services/link-building" },
      { title: "Search Engine Marketing", href: "/services/search-engine-marketing" },
      { title: "SEO Services", href: "/services/seo-services" },
      { title: "Viral Marketing", href: "/services/viral-marketing" },
    ],
  },
  {
    code: "SC09",
    title: "Social Media Intelligence",
    description: "Cross-platform social analytics, engagement engineering, and amplification at computational scale.",
    href: "/services/social-media-intelligence",
    icon: "Share2",
    accent: "cyan",
    children: [
      { title: "Social Media Management", href: "/services/social-media-management" },
      { title: "Social Media Marketing", href: "/services/social-media-marketing" },
    ],
  },
];

export interface ServiceStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const servicesStats: ServiceStat[] = [
  { value: "384×", label: "Faster Response", to: 384, suffix: "×", icon: "Zap", accent: "crimson" },
  { value: "85–95%", label: "Crisis Success Rate", to: 95, suffix: "%", icon: "ShieldCheck", accent: "emerald" },
  { value: "500+", label: "Crisis Playbooks", to: 500, suffix: "+", icon: "BookOpen", accent: "amber" },
  { value: "10", label: "Service Verticals", to: 10, icon: "LayoutGrid", accent: "purple" },
  { value: "300+", label: "Elite Clients", to: 300, suffix: "+", icon: "Crown", accent: "cyan" },
];

export interface ServiceFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const servicesFeatures: ServiceFeature[] = [
  {
    title: "Autonomous Operation",
    description: "Each service runs on the same Perception X2 platform — autonomous, AI-orchestrated, and continuously learning.",
    icon: "Cpu",
    accent: "crimson",
  },
  {
    title: "144-Dimensional Analysis",
    description: "Every service benefits from 144-dimensional emotional resonance mapping — far beyond conventional sentiment analysis.",
    icon: "Activity",
    accent: "purple",
  },
  {
    title: "95–98% Organic Authenticity",
    description: "Content deployed through any service maintains the platform's signature organic authenticity rating.",
    icon: "BadgeCheck",
    accent: "emerald",
  },
  {
    title: "50+ Platform Coverage",
    description: "Services operate across social, search, news, forums, reviews, video, podcasts, and emerging digital surfaces.",
    icon: "Globe",
    accent: "cyan",
  },
  {
    title: "15+ Years of Refinement",
    description: "Every service inherits 15+ years of operational refinement across the world's most demanding engagements.",
    icon: "Clock",
    accent: "amber",
  },
  {
    title: "Multi-Continent Operations",
    description: "Services are calibrated to cultural, regulatory, and linguistic nuance across global regions.",
    icon: "Map",
    accent: "denim",
  },
  {
    title: "Defense-Grade Security",
    description: "Zero security incidents in 15+ years. AES-256, zero trust, SOC 2 Type II, ISO 27001 across all services.",
    icon: "Lock",
    accent: "violet",
  },
  {
    title: "Coordinated Service Mesh",
    description: "Services compose into a unified operational mesh — no silos, no gaps, no handoff delays.",
    icon: "Workflow",
    accent: "rose",
  },
];

export interface ServiceUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const servicesUseCases: ServiceUseCase[] = [
  {
    title: "Government & Sovereign",
    body: "National narrative management, diplomatic communication, civic trust engineering.",
    icon: "Landmark",
    accent: "crimson",
  },
  {
    title: "Fortune 500 Enterprise",
    body: "Brand reputation, competitive positioning, market perception engineering.",
    icon: "Building2",
    accent: "purple",
  },
  {
    title: "Political Organizations",
    body: "Campaign perception, voter sentiment, public discourse shaping.",
    icon: "Vote",
    accent: "amber",
  },
  {
    title: "Public Figures & HNWIs",
    body: "Personal brand protection, legacy management, privacy at scale.",
    icon: "Crown",
    accent: "cyan",
  },
  {
    title: "Defense & Intelligence",
    body: "Strategic communication, information operations, threat neutralization.",
    icon: "Shield",
    accent: "denim",
  },
  {
    title: "Financial Institutions",
    body: "Investor perception, regulatory positioning, market confidence.",
    icon: "LineChart",
    accent: "emerald",
  },
];

export interface ServiceWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const servicesWhy: ServiceWhy[] = [
  {
    title: "Service Composability",
    body: "Every service operates on a shared platform. Compose any combination — they integrate at runtime, not via handoff documents.",
    metric: "10/10",
    accent: "crimson",
  },
  {
    title: "Sub-Hour Operational",
    body: "Crisis-class deployments reach operational status in under 60 minutes from strategic sign-off.",
    metric: "<60m",
    accent: "purple",
  },
  {
    title: "300–500% Penetration",
    body: "Documented, repeatable outcome: 300–500% improvement in narrative penetration over organic baseline.",
    metric: "412%",
    accent: "cyan",
  },
  {
    title: "95–98% Authenticity",
    body: "Content is statistically indistinguishable from organic engagement. No detection. No suspicion.",
    metric: "98%",
    accent: "emerald",
  },
  {
    title: "15+ Years Operational",
    body: "Every service inherits 15+ years of operational refinement across 300+ elite engagements.",
    metric: "15+ yrs",
    accent: "amber",
  },
  {
    title: "Zero Security Incidents",
    body: "Defense and intelligence-grade protection across every service. 15+ years without a breach.",
    metric: "0",
    accent: "denim",
  },
];

export interface ServiceFaq {
  q: string;
  a: string;
}

export const servicesFaq: ServiceFaq[] = [
  {
    q: "What is included in the Perception X2 services catalog?",
    a: "Perception X2 offers 10 top-level service verticals (brand protection, competitive intelligence, content engineering, crisis management, digital intelligence, influencer orchestration, media relations, reputation management, search dominance, and social media intelligence) — each broken into subcategories. In total, 40+ specialized services operate on the same autonomous platform.",
  },
  {
    q: "How are services priced and packaged?",
    a: "Services are packaged by engagement scope: project-based (single initiative), retainer (continuous operation), or platform (full autonomous access). Pricing reflects scale, sensitivity, and strategic depth. Detailed engagement scoping begins with a confidential briefing.",
  },
  {
    q: "Can multiple services be combined?",
    a: "Yes. Services compose into a unified operational mesh. A typical enterprise engagement combines crisis management, digital intelligence, brand protection, and search dominance — coordinated through a single strategic interface.",
  },
  {
    q: "How fast can a service be operational?",
    a: "Crisis-class deployments reach operational status in under 60 minutes from strategic sign-off. Standard engagements reach full operational capacity in 4–6 weeks, with initial impact measurable in 2–4 weeks.",
  },
  {
    q: "Are services available globally?",
    a: "Yes. Perception X2 operates across multiple continents with local expertise in cultural, regulatory, and linguistic nuance. Services are calibrated to the regional dynamics of each engagement.",
  },
  {
    q: "What industries do you serve?",
    a: "Perception X2 serves governments, Fortune 500 enterprises, political organizations, public figures, defense and intelligence agencies, financial institutions, healthcare providers, and technology companies — across virtually every industry where perception determines outcome.",
  },
  {
    q: "How is success measured?",
    a: "Each service has domain-specific KPIs calibrated during engagement scoping: narrative penetration (300–500% over baseline), organic authenticity (95–98%), response time (sub-hour), sentiment shifts, share of voice, and bespoke metrics defined per engagement.",
  },
  {
    q: "How do you ensure service quality?",
    a: "Every service runs on the Perception X2 platform — the same nine proprietary AI engines, the same 144-dimensional analytical mesh, the same 95–98% authenticity floor. Quality is not a separate process; it is built into the platform.",
  },
];

export const servicesCta = {
  eyebrow: "Begin Engagement",
  title: "The full service catalog. One autonomous platform.",
  description:
    "Perception X2 services compose into a unified operational mesh — across brand protection, crisis response, narrative engineering, intelligence monitoring, and every discipline where perception determines outcome.",
  primary: { label: "Request a Services Briefing", href: "/contact" },
  secondary: { label: "Explore the Platform", href: "/platform" },
  trustItems: [
    "10 service verticals",
    "9 subcategory hubs",
    "40+ specialized services",
    "500+ crisis playbooks",
    "Multi-continent operations",
  ],
};
